import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { needsOptimization } from '../utils/userPerformanceOptimizer';
import { useLocation } from '../context/LocationContext';
import { useTour } from '../context/TourContext';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents, CircleMarker, Polyline, Circle } from 'react-leaflet';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import MarkerClusterGroup from './MarkerClusterGroup';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import './markerClusterStyles.css';
import api from '../api';
import axios from 'axios';
import { validateNoProfanity } from '../utils/profanityFilter';
import ModeratorRequestForm from './ModeratorRequestForm';
import UserInbox from './UserInbox';
import InstallAppCTA from './InstallAppCTA';
// Modern Lucide React icons
import {
  Shield,
  MapPin,
  Heart,
  AlertTriangle,
  MessageCircle,
  Flag,
  Navigation,
  Users,
  Bell,
  X,
  Check,
  Search,
  Trash2,
  Ban,
  CheckCircle,
  XCircle,
  AlertCircle,
  LogOut
} from 'lucide-react';
import './locationButtonStyles.css';
import 'leaflet/dist/leaflet.css';
import './reportUpvoteStyles.css'; // Import styles for report upvotes
import './commentStyles.css'; // Import styles for comments
import './modalStyles.css'; // Import styles for modals
import './mapScreenStyles.css'; // Import styles for draggable search panel
import './alertsPanelStyles.css'; // Import styles for alerts bell and panel

// PERFORMANCE: Static safety resources moved outside component to prevent re-creation
const GLOBAL_SAFETY_RESOURCES = [
  // NORTH AMERICA - Boston
  { id: 'support-boston-1', name: "Boston Women's Support Center", type: "support", lat: 42.3601, lng: -71.0589, description: "Support services for women in Boston" },
  { id: 'health-boston-1', name: "Boston Women's Health Clinic", type: "health", lat: 42.3505, lng: -71.0809, description: "Healthcare services for women in Boston" },
  { id: 'emergency-boston-1', name: "Boston Crisis Response Unit", type: "emergency", lat: 42.3662, lng: -71.0621, description: "24/7 emergency services in Boston" },
  { id: 'community-boston-1', name: "Boston Women's Resource Center", type: "community", lat: 42.3471, lng: -71.0703, description: "Community resources for women in Boston" },
  // San Francisco
  { id: 'support-sf-1', name: "Women's Building Resource Center", type: "support", lat: 37.7564, lng: -122.4211, description: "A women-led community space" },
  { id: 'health-sf-1', name: "Women's Community Clinic SF", type: "health", lat: 37.7749, lng: -122.4194, description: "Free healthcare services for women" },
  { id: 'emergency-sf-1', name: "SF Crisis Support Services", type: "emergency", lat: 37.7749, lng: -122.4194, description: "24-hour emergency support" },
  { id: 'community-sf-1', name: "SF Women's Building", type: "community", lat: 37.7564, lng: -122.4211, description: "Community space for women's events" },
  // San Jose
  { id: 'support-sj-1', name: "Next Door Solutions", type: "support", lat: 37.3382, lng: -121.8863, description: "Support services for domestic violence survivors" },
  { id: 'health-sj-1', name: "Santa Clara Valley Women's Clinic", type: "health", lat: 37.3352, lng: -121.8811, description: "Comprehensive women's healthcare" },
  // New York
  { id: 'support-ny-1', name: "NYC Women's Center", type: "support", lat: 40.7128, lng: -74.0060, description: "Support services for women in NYC" },
  { id: 'health-ny-1', name: "Manhattan Women's Health", type: "health", lat: 40.7831, lng: -73.9712, description: "Healthcare services for women" },
  // Chicago
  { id: 'support-chi-1', name: "Chicago Women's Support", type: "support", lat: 41.8781, lng: -87.6298, description: "Support services for women in Chicago" },
  { id: 'health-chi-1', name: "Chicago Women's Health Center", type: "health", lat: 41.8562, lng: -87.6226, description: "Healthcare services for women" },
  // London
  { id: 'support-london-1', name: "London Women's Centre", type: "support", lat: 51.5074, lng: -0.1278, description: "Support services for women in London" },
  // Tokyo
  { id: 'support-tokyo-1', name: "Tokyo Women's Support", type: "support", lat: 35.6762, lng: 139.6503, description: "Support services for women in Tokyo" },
  // Sydney
  { id: 'support-sydney-1', name: "Sydney Women's Support", type: "support", lat: -33.8688, lng: 151.2093, description: "Support services for women in Sydney" },
];

// Fix for Leaflet marker icons

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Default center on wider Bay Area
const defaultCenter = {
  lat: 37.640,
  lng: -122.140
};

const MapInteractionHandler = ({ onUserInteract }) => {
  // Using _ to indicate we're intentionally not using this variable
  // but need it for the events to register
  // eslint-disable-next-line no-unused-vars
  const _ = useMapEvents({
    dragstart: () => onUserInteract?.(),
    zoomstart: () => onUserInteract?.(),
    movestart: () => onUserInteract?.()
  });

  return null;
};

// Custom component to set the map view based on center coordinates (only for search results)
const SetViewOnCenter = ({ center, isSearchTriggered }) => {
  const map = useMap();
  const lastCenterRef = useRef(null);
  
  useEffect(() => {
    // Only update if this is from a search (not automatic location updates)
    if (center && isSearchTriggered && center !== lastCenterRef.current) {
      lastCenterRef.current = center;
      map.setView([center.lat, center.lng], 14, {
        animate: true,
        duration: 1
      });
    }
  }, [center, map, isSearchTriggered]);
  
  return null;
};

// Component to handle map clicks for reporting
function MapClickHandler({ isReportMode, onMapClick }) {
  // Using _ to indicate we're intentionally not using this variable
  // but need it for the events to register
  // eslint-disable-next-line no-unused-vars
  const _ = useMapEvents({
    click: (e) => {
      if (isReportMode) {
        onMapClick(e.latlng);
      }
    }
  });
  
  return null;
}

// Service location types with their shapes for the map
const serviceLocationTypes = [
  { type: "support", label: "Support Centers", shape: "star" },
  { type: "health", label: "Health Services", shape: "diamond" },
  { type: "emergency", label: "Emergency Services", shape: "triangle" },
  { type: "community", label: "Community Spaces", shape: "square" }
];

const MapScreen = ({
  user,
  onNavigateToProfile,
  onNavigateToReports,
  onNavigateToModeratorDashboard,
  onLogout,
  alertRadius = 500,
  locationRefreshInterval = 10,
  alertsMuted = false,
  trackingMode = false,
  showAccuracyCircle = false,
  showLocationTrail = false,
  showModeratorCoverageCircle = false,
  // Tour mode props
  isTourMode = false,
  tourReports = [],
}) => {
  // Safety locations that will be dynamically updated based on user location
  const [_safetyLocations, setSafetyLocations] = useState([
    // Initial sample data - will be replaced with location-specific data
    { id: 1, name: "Women's Building Resource Center", type: "support", lat: 37.7564, lng: -122.4211, description: "A women-led community space that advocates for self-determination, gender equality and social justice" },
    { id: 10, name: "Women's Community Clinic", type: "health", lat: 37.7749, lng: -122.4194, description: "Free healthcare services for women" },
    { id: 18, name: "Crisis Support Services", type: "emergency", lat: 37.7749, lng: -122.4194, description: "24-hour emergency support" },
    { id: 26, name: "Community Center", type: "community", lat: 37.7564, lng: -122.4211, description: "Community space for women's events and resources" }
  ]);

  const [center, setCenter] = useState(defaultCenter);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);
  // Get userLocation and functions from LocationContext
  const { userLocation, locationError, refreshLocation } = useLocation();

  // Tour context — signals sent from TourOverlay to trigger map actions
  const { tourSignal, clearSignal } = useTour();
  
  // Apply user-specific performance optimizations if needed
  const userNeedsOptimization = needsOptimization(user);
  
  // For users with flashing issues, use optimization settings
  useEffect(() => {
    if (userNeedsOptimization) {
      console.log(`Applying performance optimizations for user ${user.id}`);
      // Apply CSS fix for flickering
      document.body.classList.add('optimized-rendering');
      
      // Return cleanup function
      return () => {
        document.body.classList.remove('optimized-rendering');
      };
    }
  }, [user, userNeedsOptimization]);

  useEffect(() => {
    const isModerator = user && (user.role === 'moderator' || user.moderatorActive);
    if (!isModerator || !showModeratorCoverageCircle) {
      setModeratorCoverage(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/moderator/my-coverage');
        if (!cancelled) {
          setModeratorCoverage(res.data);
        }
      } catch (e) {
        if (!cancelled) {
          setModeratorCoverage(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, showModeratorCoverageCircle]);
  const [reportMarkers, setReportMarkers] = useState([]);
  const [isReportMode, setIsReportMode] = useState(false);
  const [showReportModeGuide, setShowReportModeGuide] = useState(false);
  const [reportFormVisible, setReportFormVisible] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [reportType, setReportType] = useState('level_1');
  const [reportDescription, setReportDescription] = useState('');
  const reportDescriptionRef = useRef(''); // PERFORMANCE: Use ref for immediate value access
  // All reports are always anonymous for user safety - no option to reveal identity
  const isAnonymous = true;
  // eslint-disable-next-line no-unused-vars
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [isResolvingReportAddress, setIsResolvingReportAddress] = useState(false);
  const reportClickLockRef = useRef(false);
  const reverseGeocodeRequestIdRef = useRef(0);

  const [moderatorCoverage, setModeratorCoverage] = useState(null);
  
  useEffect(() => {
    setShowReportModeGuide(isReportMode);
  }, [isReportMode]);
  const [commentingOnReport, setCommentingOnReport] = useState(null);
  const [commentText, setCommentText] = useState('');
  const commentTextRef = useRef(''); // PERFORMANCE: Use ref for immediate value access
  const [showCommentForm, setShowCommentForm] = useState(false);
  // All comments are anonymous by default
  const isCommentAnonymous = true; // No longer a state variable, always true
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [_mapBounds, setMapBounds] = useState(null);
  const mapRef = useRef(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [confirmUnupvoteReportId, setConfirmUnupvoteReportId] = useState(null);
  const [showResourcesModal, setShowResourcesModal] = useState(false);
  const [level2Subtype, setLevel2Subtype] = useState('invasive_questions');
  const [level1Subtype, setLevel1Subtype] = useState('cat_calling');
  const [level3Subtype, setLevel3Subtype] = useState('drink_tampering');
  const [reportsConfig, setReportsConfig] = useState(null);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const [showAlertsPanel, setShowAlertsPanel] = useState(false);
  const [showModeratorRequest, setShowModeratorRequest] = useState(false);
  const [showUserInbox, setShowUserInbox] = useState(false);
  const [userInboxReportId, setUserInboxReportId] = useState(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [_totalMessagesCount, setTotalMessagesCount] = useState(0);
  const [pendingModeratorResponsesCount, setPendingModeratorResponsesCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/reports/config');
        if (!cancelled) {
          setReportsConfig(res.data || null);
        }
      } catch (e) {
        if (!cancelled) {
          setReportsConfig(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const severityOptions = useMemo(() => {
    const rows = reportsConfig?.severityTypes;
    if (Array.isArray(rows) && rows.length) {
      return rows
        .filter(r => r && r.type)
        .slice()
        .sort((a, b) => (Number(a.severityRank) || 0) - (Number(b.severityRank) || 0));
    }
    return [
      { type: 'level_1', severityRank: 1, label: 'Level 1: Low Severity - Discomfort/Unease' },
      { type: 'level_2', severityRank: 2, label: 'Level 2: Medium Severity - Verbal Harassment' },
      { type: 'level_3', severityRank: 3, label: 'Level 3: High Severity - Targeted Behavior' }
    ];
  }, [reportsConfig]);

  const getSeveritySelectLabel = useCallback((t) => {
    const hit = severityOptions.find(s => s.type === t);
    if (!hit) return t;
    if (hit.label && hit.label.toLowerCase().includes('level')) return hit.label;
    if (t === 'level_1') return `Level 1: ${hit.label}`;
    if (t === 'level_2') return `Level 2: ${hit.label}`;
    if (t === 'level_3') return `Level 3: ${hit.label}`;
    return hit.label || t;
  }, [severityOptions]);

  const subtypeOptionsForReportType = useMemo(() => {
    const m = reportsConfig?.subtypesBySeverity;
    const list = m && reportType ? m[reportType] : null;
    if (Array.isArray(list) && list.length) {
      return list.filter(x => x && x.key && x.label);
    }
    if (reportType === 'level_1') {
      return [
        { key: 'cat_calling', label: 'Cat calling' },
        { key: 'infrastructure_hazards', label: 'Infrastructure & Safety Hazards (broken streetlights, abandoned buildings)' },
        { key: 'no_security_cameras', label: 'Public places without working security cameras' },
        { key: 'other_level1', label: 'Other' }
      ];
    }
    if (reportType === 'level_2') {
      return [
        { key: 'invasive_questions', label: 'Invasive questions (e.g., "Are you alone?" "Where do you live?")' },
        { key: 'cornered_no_contact', label: 'Being cornered with no physical contact' },
        { key: 'persistent_following', label: 'Being followed persistently after refusal to engage' },
        { key: 'service_refusal', label: 'Business refusing service or entry based on gender' },
        { key: 'other_level2', label: 'Other' }
      ];
    }
    if (reportType === 'level_3') {
      return [
        { key: 'drink_tampering', label: 'Drink tampering or suspicion of being drugged' },
        { key: 'coercion_to_leave', label: 'Someone trying to coerce you to leave the venue' },
        { key: 'verbal_threats', label: 'Verbal threats, aggressive confrontation, or hate speech' },
        { key: 'kidnapping_attempt', label: 'Kidnapping attempt or being pulled toward a vehicle' },
        { key: 'other_level3', label: 'Other' }
      ];
    }
    return [];
  }, [reportsConfig, reportType]);

  const currentSubtypeValue = useMemo(() => {
    if (reportType === 'level_1') return level1Subtype;
    if (reportType === 'level_2') return level2Subtype;
    if (reportType === 'level_3') return level3Subtype;
    return '';
  }, [reportType, level1Subtype, level2Subtype, level3Subtype]);

  const setCurrentSubtypeValue = useCallback((nextValue) => {
    if (reportType === 'level_1') return setLevel1Subtype(nextValue);
    if (reportType === 'level_2') return setLevel2Subtype(nextValue);
    if (reportType === 'level_3') return setLevel3Subtype(nextValue);
  }, [reportType]);

  useEffect(() => {
    const keys = (subtypeOptionsForReportType || []).map(o => o.key);
    if (!keys.length) return;

    if (reportType === 'level_1') {
      if (!keys.includes(level1Subtype)) setLevel1Subtype(keys[0]);
    } else if (reportType === 'level_2') {
      if (!keys.includes(level2Subtype)) setLevel2Subtype(keys[0]);
    } else if (reportType === 'level_3') {
      if (!keys.includes(level3Subtype)) setLevel3Subtype(keys[0]);
    }
  }, [reportType, subtypeOptionsForReportType, level1Subtype, level2Subtype, level3Subtype]);

  // Keep marker refs by reportId to allow programmatically opening popups (from Inbox -> View on map)
  const reportMarkerRefs = useRef(new Map());
  // Initialize moderator request status from localStorage if available
  const [_moderatorRequestStatus, setModeratorRequestStatus] = useState(
    localStorage.getItem('moderatorRequestStatus') || 'none'
  ); // null, 'pending', 'approved', 'denied'
  
  // Profanity attempt tracking
  const [profanityAttempts, setProfanityAttempts] = useState(0);
  const [commentProfanityAttempts, setCommentProfanityAttempts] = useState(0);
  
  // Draggable search panel states
  const [panelHeight, setPanelHeight] = useState(80); // Initial height in pixels (collapsed)
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startHeight, setStartHeight] = useState(80);
  const panelRef = useRef(null);  
  
  // Live tracking states (Uber/Waze-like features)
  const [locationHistory, setLocationHistory] = useState([]); // Trail path
  // Note: trackingMode, showAccuracyCircle, showLocationTrail now come as props from parent
  
  // Proximity alerts state
  const [alertedReports, setAlertedReports] = useState(new Set()); // Track which reports we've alerted for
  const [nearbyReports, setNearbyReports] = useState([]); // Currently nearby reports
  const [acknowledgedReports, setAcknowledgedReports] = useState(() => {
    // Load acknowledged reports from localStorage
    const stored = localStorage.getItem('acknowledgedReports');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });
  const [currentAlertReportId, setCurrentAlertReportId] = useState(null); // Track which report triggered current alert
  
  // Restart location tracking when refresh interval changes
  useEffect(() => {
    // Store the interval so LocationContext can read it
    localStorage.setItem('locationRefreshInterval', locationRefreshInterval);
    
    // Refresh location tracking to apply new settings
    if (refreshLocation) {
      refreshLocation();
    }
  }, [locationRefreshInterval, refreshLocation]);
  
  // Use user's location from profile if available
  const locationInputRef = useRef(null);
  const searchTimeout = useRef(null);
  const suppressReportFetchUntilRef = useRef(0);
  const isAnyPopupOpenRef = useRef(false);

  // ── Tour signal handler ──────────────────────────────────────────────────
  // TourOverlay fires signals via TourContext; we react here to drive the map
  useEffect(() => {
    if (!tourSignal || !isTourMode) return;

    if (tourSignal === 'open_spotlight') {
      // Pan to Powell St BART (tour-001) and open its popup
      const SPOTLIGHT_ID = 'tour-001';
      const SPOTLIGHT_LAT = 37.7844;
      const SPOTLIGHT_LNG = -122.4079;
      if (mapRef.current) {
        try {
          mapRef.current.setView([SPOTLIGHT_LAT, SPOTLIGHT_LNG], 16, { animate: true });
        } catch (e) { /* ignore */ }
      }
      // Delay slightly to let the map pan before opening popup
      setTimeout(() => {
        const marker = reportMarkerRefs.current.get(SPOTLIGHT_ID);
        if (marker && typeof marker.openPopup === 'function') {
          try { marker.openPopup(); } catch (e) { /* ignore */ }
        }
      }, 600);
      clearSignal();
    }

    if (tourSignal === 'open_create_report') {
      // Activate report mode at a central SF location
      if (mapRef.current) {
        try {
          mapRef.current.setView([37.7749, -122.4194], 15, { animate: true });
        } catch (e) { /* ignore */ }
      }
      setIsReportMode(true);
      clearSignal();
    }

    if (tourSignal === 'end') {
      clearSignal();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourSignal, isTourMode]);

  const openReportPopupFromInbox = ({ reportId, lat, lng }) => {
    if (!mapRef.current) return;

    // Pan to location quickly for context
    if (typeof lat === 'number' && typeof lng === 'number') {
      try {
        mapRef.current.setView([lat, lng], Math.max(mapRef.current.getZoom() || 13, 16), { animate: true });
      } catch (e) {
        // Ignore
      }
    }

    // Try to open the popup if we have the marker instance
    const marker = reportMarkerRefs.current.get(reportId);
    if (marker && typeof marker.openPopup === 'function') {
      try {
        suppressReportFetchUntilRef.current = Date.now() + 1200;
        marker.openPopup();
        return;
      } catch (e) {
        // Ignore and fall through
      }
    }

    // Fallback: ensure report details are set so when user sees the map, context is still present
    const found = (reportMarkers || []).find(r => r && r.id === reportId);
    if (found) {
      setCurrentReport(found);
    }
  };
  
  // Fetch reports only within the current map bounds
  // Stable ref so map event listeners don't re-register when this function changes
  const fetchReportsRef = useRef(null);

  const fetchReports = useCallback(async (bounds = null) => {
    // Tour mode: use simulation data instead of the real API
    if (isTourMode) {
      const transformed = tourReports.map(r => ({
        ...r,
        position: { lat: r.latitude, lng: r.longitude },
        lat: r.latitude,
        lng: r.longitude,
      }));
      setReportMarkers(transformed);
      return;
    }

    if (!mapRef.current && !bounds) return;

    try {
      const currentBounds = bounds || mapRef.current.getBounds();
      const response = await api.get('/api/reports/bounds', {
        params: {
          north: currentBounds.getNorth(),
          south: currentBounds.getSouth(),
          east: currentBounds.getEast(),
          west: currentBounds.getWest(),
          limit: 500
        }
      });

      const incoming = response.data.map(report => ({
        ...report,
        position: { lat: report.lat, lng: report.lng }
      }));

      // Merge: only update state if something actually changed — prevents full re-render flash
      setReportMarkers(prev => {
        if (!prev || prev.length === 0) return incoming;
        const prevIds = new Set(prev.map(r => r.id));
        const nextIds = new Set(incoming.map(r => r.id));
        const same =
          prev.length === incoming.length &&
          incoming.every(r => prevIds.has(r.id)) &&
          prev.every(r => nextIds.has(r.id));
        if (same) return prev; // No change — skip re-render
        return incoming;
      });
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  }, [mapRef, isTourMode, tourReports]);

  // Keep ref in sync so event handlers always call latest version without re-registering
  useEffect(() => { fetchReportsRef.current = fetchReports; }, [fetchReports]);
  
  // Disabled safety resources loading
  // useEffect(() => {
  //   fetchSafetyResources();
  // }, [fetchSafetyResources]);

  // Track if we've already shown location error to prevent spam
  const locationErrorShownRef = useRef(false);
  const initialCenterSetRef = useRef(false);
  
  // Handle initial map load and center setting (runs once)
  useEffect(() => {
    // Wait for location to be available or error out
    if (userLocation && !initialCenterSetRef.current) {
      initialCenterSetRef.current = true;
      
      // Center the map on user's location (only once on initial load)
      setCenter({ ...userLocation, isSet: true });
      
      // Small delay to ensure map is ready before setting view
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.setView([userLocation.lat, userLocation.lng], 13, {
            animate: false // No animation on initial load to prevent flicker
          });
          
          // Get initial bounds
          const bounds = mapRef.current.getBounds();
          const initialBounds = {
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest()
          };
          setMapBounds(initialBounds);
          setMapReady(true);
        }
        setIsInitialLoad(false);
      }, 100);
    }
    
    // Show error ONCE if location service failed (not repeatedly)
    if (locationError && !locationErrorShownRef.current) {
      locationErrorShownRef.current = true;
      // Only show modal if it's a critical error (not default location fallback)
      if (!userLocation?.isDefault) {
        console.warn('Location unavailable:', locationError);
      }
      setIsInitialLoad(false);
    }
  }, [userLocation, locationError]);
  
  // Add map event listeners for viewport changes
  useEffect(() => {
    if (!mapRef.current) return;

    const handlePopupOpen = () => {
      isAnyPopupOpenRef.current = true;
    };

    const handlePopupClose = () => {
      isAnyPopupOpenRef.current = false;
    };
    
    // Handler for map moveend event (triggered after panning/zooming)
    const handleMoveEnd = () => {
      if (!mapRef.current) return;

      // If a popup is currently open, avoid refreshing markers underneath it.
      if (isAnyPopupOpenRef.current) {
        return;
      }

      // If the movement was caused by our popup-offset panning, don't refetch markers.
      if (Date.now() < suppressReportFetchUntilRef.current) {
        return;
      }
      
      const bounds = mapRef.current.getBounds();
      setMapBounds({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      });
      
      // Debounce — 600ms prevents flashing during continuous pan/zoom
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(() => {
        if (fetchReportsRef.current) fetchReportsRef.current(bounds);
      }, 600);
    };
    
    // Initial fetch when map is ready
    if (mapReady) {
      if (fetchReportsRef.current) fetchReportsRef.current();
    }
    
    // Listen for map movement to update markers
    mapRef.current.on('popupopen', handlePopupOpen);
    mapRef.current.on('popupclose', handlePopupClose);
    mapRef.current.on('moveend', handleMoveEnd);
    
    // Set up interval to periodically refresh reports (every 90 seconds - reduced for performance)
    // This ensures we get new reports even if the user doesn't move the map
    const interval = setInterval(() => {
      if (mapRef.current) {
        if (isAnyPopupOpenRef.current) {
          return;
        }
        if (Date.now() < suppressReportFetchUntilRef.current) {
          return;
        }
        const bounds = mapRef.current.getBounds();
        if (fetchReportsRef.current) fetchReportsRef.current(bounds);
      }
    }, 90000);
    
    // Clean up event listeners and interval on unmount
    return () => {
      if (mapRef.current) {
        mapRef.current.off('popupopen', handlePopupOpen);
        mapRef.current.off('popupclose', handlePopupClose);
        mapRef.current.off('moveend', handleMoveEnd);
      }
      clearInterval(interval);
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, mapRef]);
  
  // Check for unread moderator messages
  // PERFORMANCE: Reduced polling from 30s to 120s (2 minutes)
  useEffect(() => {
    const checkUnreadCount = async () => {
      try {
        const response = await api.get('/api/user/messages/unread-count');
        setUnreadMessagesCount(response.data.count || 0);
        setTotalMessagesCount(response.data.total || 0);
      } catch (err) {
        // Silently fail - user may not have any messages
      }
    };
    
    if (user) {
      checkUnreadCount();
      // Check every 120 seconds (reduced for performance)
      const interval = setInterval(checkUnreadCount, 120000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Pending moderator responses count (badge on moderator button)
  useEffect(() => {
    const checkPendingModeratorResponses = async () => {
      try {
        const response = await api.get('/api/moderator/messages/pending-count');
        setPendingModeratorResponsesCount(response.data.count || 0);
      } catch (err) {
        // Silently fail - not a moderator or endpoint unavailable
      }
    };

    const hasModAccess = user?.role === 'admin' || (user?.role === 'moderator' && user?.moderatorActive);
    if (hasModAccess) {
      checkPendingModeratorResponses();
      const interval = setInterval(checkPendingModeratorResponses, 120000);
      return () => clearInterval(interval);
    }
  }, [user?.role, user?.moderatorActive]);

  const openModeratorMessages = async () => {
    try {
      await api.post('/api/moderator/messages/mark-reviewed');
      setPendingModeratorResponsesCount(0);

    } catch (err) {
      // Ignore
    }
    window.location.href = '/moderator/dashboard?tab=messages';
  };

  // Check moderator request status and refresh user data
  useEffect(() => {
    const checkModeratorRequestStatus = async () => {
      try {
        // Always fetch fresh status from server
        const response = await api.get('/api/user/moderator-request-status');
        console.log('Moderator request status from server:', JSON.stringify(response.data));

        // If server indicates moderator access is disabled, force a local logout/downgrade
        if (response.data?.disabled || response.data?.role === 'user') {
          localStorage.removeItem('token');
          const userStr = localStorage.getItem('user');
          if (userStr) {
            try {
              const userData = JSON.parse(userStr);
              userData.role = 'user';
              userData.moderatorActive = false;
              userData.moderator = false;
              localStorage.setItem('user', JSON.stringify(userData));
            } catch (e) {
              localStorage.removeItem('user');
            }
          }
          localStorage.setItem('moderatorRequestStatus', response.data?.status || 'none');
          window.location.reload();
          return;
        }
        
        // Always update state with latest status
        setModeratorRequestStatus(response.data.status);
        localStorage.setItem('moderatorRequestStatus', response.data.status || 'none');
        
        // If approved with enhanced response (including token)
        if (response.data.status === 'approved') {
          console.log('Moderator request is approved, updating user data');

          // If server returned a new token with updated role
          if (response.data.token) {
            console.log('Server provided updated token with moderator role');
            
            // Update token in localStorage
            localStorage.setItem('token', response.data.token);
            
            // Get current user object
            const userStr = localStorage.getItem('user');
            if (userStr) {
              const userData = JSON.parse(userStr);
              
              // Update user role directly
              userData.role = 'moderator';
              userData.moderatorActive = true;
              userData.moderator = true;
              localStorage.setItem('user', JSON.stringify(userData));
              
              console.log('User object updated with moderator role in localStorage');
              
              // Force reload if requested by server
              if (response.data.forceReload) {
                console.log('Forcing page reload to reflect role change');
                window.location.reload();
                return;
              }
            }
          } else {
            // Fall back to previous approach with auth endpoint
            const authResponse = await api.get('/api/auth');
            if (authResponse.data.user) {
              const updatedUser = authResponse.data.user;
              console.log('Updated user data from auth endpoint:', updatedUser);
              
              // Update localStorage with fresh user data
              localStorage.setItem('user', JSON.stringify(updatedUser));
              
              // Force reload to update user state in App.js (setUser not available here)
              if (updatedUser.role === 'moderator' && user.role !== 'moderator') {
                console.log('User role updated to moderator, reloading page');
                window.location.reload();
              }
            }
          }
        }
      } catch (err) {
        console.error('Error checking moderator status:', err);
        // Don't reset status on error if we have it cached
        if (!localStorage.getItem('moderatorRequestStatus')) {
          setModeratorRequestStatus(null);
        }
      }
    };

    const hasModeratorDashboardAccess =
      user && (user.role === 'admin' || (user.role === 'moderator' && user?.moderatorActive));

    if (user && !hasModeratorDashboardAccess) {
      checkModeratorRequestStatus();
    }
  }, [user]);
  
  // Track location history for trail path (Uber/Waze-like) - only real GPS locations
  useEffect(() => {
    // Only track history if we have real GPS (not default location)
    if (userLocation && !userLocation.isDefault && userLocation.accuracy) {
      setLocationHistory(prev => {
        const newHistory = [...prev, {
          lat: userLocation.lat,
          lng: userLocation.lng,
          timestamp: userLocation.timestamp
        }];
        // Keep last 100 points (adjust for performance)
        return newHistory.slice(-100);
      });
    }
  }, [userLocation]);
  
  // Auto-center map in tracking mode (Uber/Waze-like) - smooth, non-aggressive
  const lastPanRef = useRef(null);
  const userInteractedWithMapRef = useRef(false);
  
  useEffect(() => {
    // Don't auto-center if:
    // 1. Still on initial load (prevents flicker)
    // 2. Using default location (no GPS)
    // 3. Map not ready yet
    if (isInitialLoad || !mapReady) {
      return;
    }
    
    if (trackingMode && !userInteractedWithMapRef.current && userLocation && !userLocation.isDefault && mapRef.current) {
      const map = mapRef.current;
      const currentCenter = map.getCenter();
      
      // Calculate distance from current center to new location (in meters)
      const distance = calculateDistance(
        currentCenter.lat,
        currentCenter.lng,
        userLocation.lat,
        userLocation.lng
      ) * 1000;
      
      // Only recenter if:
      // 1. User has moved more than 200 meters from map center (increased from 100m)
      // 2. Haven't panned in the last 5 seconds (increased from 2s to prevent dizziness)
      const now = Date.now();
      const timeSinceLastPan = lastPanRef.current ? now - lastPanRef.current : Infinity;
      
      if (distance > 200 && timeSinceLastPan > 5000) {
        // Smooth pan without changing zoom
        map.panTo(
          [userLocation.lat, userLocation.lng],
          {
            animate: true,
            duration: 1.5, // Slightly faster animation
            easeLinearity: 0.1, // Smoother easing
            noMoveStart: true // Prevent triggering movestart event
          }
        );
        lastPanRef.current = now;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation, trackingMode]);

  const handleUserMapInteraction = useCallback(() => {
    userInteractedWithMapRef.current = true;
    lastPanRef.current = Date.now();
  }, []);

  // Listen for custom upvote events from marker popups
  useEffect(() => {
    const handleReportUpvote = (event) => {
      const { reportId } = event.detail;
      if (reportId) {
        showUpvoteCommentForm(reportId);
      }
    };

    // Add event listener
    document.addEventListener('report-upvote', handleReportUpvote);

    // Cleanup
    return () => {
      document.removeEventListener('report-upvote', handleReportUpvote);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Throttle ref for proximity alerts
  const lastProximityCheckRef = useRef(0);
  
  // Get location predictions as user types
  const getLocationPredictions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    setIsSearching(true);
    try {
      const searchParams = {
        q: query,
        format: 'json',
        limit: 30, // Get more results to filter
        addressdetails: 1 // Get detailed address information
      };
      
      // Add viewbox parameter to prioritize results near user location
      if (userLocation) {
        // Create a smaller bounding box for more local results (roughly 25km radius)
        const latOffset = 0.225; // approximately 25km
        const lngOffset = 0.225;
        searchParams.viewbox = `${userLocation.lng - lngOffset},${userLocation.lat + latOffset},${userLocation.lng + lngOffset},${userLocation.lat - latOffset}`;
        searchParams.bounded = 1; // Restrict to viewbox for more local results
      }
      
      const response = await axios.get('https://nominatim.openstreetmap.org/search', {
        params: searchParams,
        headers: {
          'Accept-Language': 'en-US,en',
          'User-Agent': 'SafeHER-App'
        }
      });
      
      // Process results
      let processedResults = response.data.map(result => {
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);
        let distance = null;
        
        if (userLocation) {
          distance = calculateDistance(userLocation.lat, userLocation.lng, lat, lng);
        }
        
        // Determine if this is a business/establishment vs a city/region
        const placeType = result.type || '';
        const placeClass = result.class || '';
        const isEstablishment = ['amenity', 'shop', 'tourism', 'leisure'].includes(placeClass) ||
                               ['restaurant', 'cafe', 'shop', 'hotel', 'hospital', 'school', 'bank', 'pharmacy'].includes(placeType);
        
        return {
          display_name: result.display_name,
          lat: lat,
          lng: lng,
          lon: lng,
          type: placeType,
          class: placeClass,
          distance: distance,
          importance: result.importance || 0,
          isEstablishment: isEstablishment
        };
      });
      
      // Filter out results that are too far away (more than 50km)
      if (userLocation) {
        processedResults = processedResults.filter(result => 
          result.distance === null || result.distance <= 50
        );
      }
      
      // Sort by distance if user location is available, with establishments prioritized
      if (userLocation) {
        processedResults.sort((a, b) => {
          // Strongly prioritize establishments over cities/regions
          if (a.isEstablishment && !b.isEstablishment) return -1;
          if (!a.isEstablishment && b.isEstablishment) return 1;
          
          // Then sort by distance
          const distanceDiff = (a.distance || Infinity) - (b.distance || Infinity);
          
          // If distances are very close (within 2km), use importance as tiebreaker
          if (Math.abs(distanceDiff) < 2) {
            return (b.importance || 0) - (a.importance || 0);
          }
          return distanceDiff;
        });
      } else {
        // If no user location, sort by importance
        processedResults.sort((a, b) => (b.importance || 0) - (a.importance || 0));
      }
      
      // Limit to top 8 results after sorting
      processedResults = processedResults.slice(0, 8);
      
      setSearchResults(processedResults);
      setShowSearchResults(processedResults.length > 0);
    } catch (error) {
      console.error('Error fetching predictions:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation]);

  // Handle drag start
  const handleDragStart = (e) => {
    setIsDragging(true);
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    setStartY(clientY);
    setStartHeight(panelHeight);
  };
  
  // Handle dragging
  const handleDrag = useCallback((e) => {
    if (!isDragging) return;
    
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    const deltaY = startY - clientY; // Positive when dragging up
    const newHeight = Math.max(80, Math.min(window.innerHeight * 0.85, startHeight + deltaY));
    
    setPanelHeight(newHeight);
  }, [isDragging, startY, startHeight]);
  
  // Handle drag end
  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Snap to positions based on height
    const windowHeight = window.innerHeight;
    const threshold = windowHeight * 0.3;
    
    if (panelHeight < threshold) {
      // Snap to collapsed (80px)
      setPanelHeight(80);
    } else if (panelHeight < windowHeight * 0.6) {
      // Snap to half
      setPanelHeight(windowHeight * 0.5);
    } else {
      // Snap to expanded
      setPanelHeight(windowHeight * 0.85);
    }
  }, [isDragging, panelHeight]);
  
  // Add event listeners for drag
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleDrag(e);
      const handleMouseUp = () => handleDragEnd();
      const handleTouchMove = (e) => handleDrag(e);
      const handleTouchEnd = () => handleDragEnd();
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleDrag, handleDragEnd]);
  
  // Calculate distance between two points using Haversine formula
  // eslint-disable-next-line no-unused-vars
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    // Validate inputs
    if (lat1 === undefined || lon1 === undefined || lat2 === undefined || lon2 === undefined) {
      console.error('Invalid coordinates for distance calculation:', { lat1, lon1, lat2, lon2 });
      return Infinity; // Return infinity to prevent alerts on invalid coords
    }

    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1); 
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2)
      ; 
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    const distance = R * c; // Distance in km
    return distance;
  };
  
  const deg2rad = (deg) => {
    return deg * (Math.PI/180);
  };
  
  // PERFORMANCE: Memoize nearby reports calculation to avoid recalculating on every render
  const nearbyReportsCalculated = useMemo(() => {
    if (!userLocation || !reportMarkers || reportMarkers.length === 0) {
      return [];
    }
    
    return reportMarkers.filter(report => {
      if (!report) return false;
      if (report.createdBy === user?.id) return false;
      if (acknowledgedReports.has(report.id)) return false;
      if (report.type === 'crime_report') return false;
      
      const distance = calculateDistance(
        userLocation.lat, userLocation.lng,
        report.position.lat, report.position.lng
      ) * 1000;
      
      return distance <= alertRadius;
    });
  }, [userLocation?.lat, userLocation?.lng, reportMarkers, alertRadius, user?.id, acknowledgedReports, calculateDistance]);
  
  // Check for nearby reports and show alerts (throttled)
  useEffect(() => {
    // Throttle: only run every 2 seconds
    const now = Date.now();
    if (now - lastProximityCheckRef.current < 2000) {
      return;
    }
    lastProximityCheckRef.current = now;
    
    if (!userLocation || nearbyReportsCalculated.length === 0) {
      return;
    }
    
    setNearbyReports(nearbyReportsCalculated);
    
    // Alert for new nearby reports (not previously alerted or acknowledged, and not muted)
    nearbyReportsCalculated.forEach(report => {
      if (!alertsMuted && !alertedReports.has(report.id) && !acknowledgedReports.has(report.id)) {
        // Mark as alerted
        setAlertedReports(prev => new Set([...prev, report.id]));
        
        // Get severity level
        const severityLabels = {
          'level_1': 'Low Severity',
          'level_2': 'Medium Severity',
          'level_3': 'High Severity'
        };
        const severityLabel = severityLabels[report.type] || report.type;
        
        // Calculate distance in meters
        const distanceInMeters = calculateDistance(
          userLocation.lat,
          userLocation.lng,
          report.position.lat,
          report.position.lng
        ) * 1000;
        
        // Show alert with both metric and US units
        const distanceInFeet = distanceInMeters * 3.28084;
        const distanceText = distanceInMeters < 1000 
          ? `${Math.round(distanceInMeters)}m (${Math.round(distanceInFeet)}ft)` 
          : `${(distanceInMeters / 1000).toFixed(1)}km (${(distanceInFeet / 5280).toFixed(1)}mi)`;
        
        setModalTitle('Nearby Report Alert');
        setModalMessage(
          `A ${severityLabel} incident was reported ${distanceText} from your location.\n\n` +
          `Description: ${report.description.substring(0, 100)}${report.description.length > 100 ? '...' : ''}\n\n` +
          `Stay alert and consider using alternative routes.`
        );
        setCurrentAlertReportId(report.id);
        setShowModal(true);
        
        // Play notification sound
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE');
          audio.volume = 0.3;
          audio.play().catch(() => {});
        } catch (e) {}
      }
    });
    
    // Clean up alerts for reports that are no longer nearby
    setAlertedReports(prev => {
      const newAlerted = new Set(prev);
      const nearbyIds = new Set(nearbyReportsCalculated.map(r => r.id));
      
      prev.forEach(reportId => {
        if (!nearbyIds.has(reportId)) {
          setTimeout(() => {
            setAlertedReports(current => {
              const updated = new Set(current);
              updated.delete(reportId);
              return updated;
            });
          }, 5 * 60 * 1000);
        }
      });
      
      return newAlerted;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearbyReportsCalculated, alertsMuted, alertedReports]);
  
  // Set selected location when a search result is clicked
  const selectLocation = (location) => {
    // Create location object from the parameter
    const locationCoords = {
      lat: parseFloat(location.lat),
      lng: parseFloat(location.lon || location.lng)
    };
    
    // Set the center coordinates to the selected location
    setCenter(locationCoords);
    setIsSearchTriggered(true);
    
    // Reset search trigger after a delay
    setTimeout(() => setIsSearchTriggered(false), 2000);
    // Update the search input field
    setSearchQuery(location.display_name);
    // Hide the dropdown
    setShowSearchResults(false);
    // Update the input reference value
    locationInputRef.current.value = location.display_name;
    
    // Show a brief visual confirmation
    setModalTitle('Location Found');
    setModalMessage(`Map view has been updated to ${location.display_name}`);
    setShowModal(true);
    setTimeout(() => setShowModal(false), 2000); // Auto-dismiss after 2 seconds
  };
  
  // Handle search form submission
  const searchLocation = async (e) => {
    if (e) e.preventDefault();
    if (!searchQuery) return;
    
    setIsSearching(true);
    try {
      // Use user location for proximity-based search if available
      const searchParams = {
        q: searchQuery,
        format: 'json',
        limit: 10
      };
      
      // Add viewbox parameter to prioritize results near user location
      if (userLocation) {
        // Create a bounding box around user location (roughly 50km radius)
        const latOffset = 0.45; // approximately 50km
        const lngOffset = 0.45;
        searchParams.viewbox = `${userLocation.lng - lngOffset},${userLocation.lat + latOffset},${userLocation.lng + lngOffset},${userLocation.lat - latOffset}`;
        searchParams.bounded = 0; // Don't restrict to viewbox, just prioritize
      }
      
      const response = await axios.get('https://nominatim.openstreetmap.org/search', {
        params: searchParams,
        headers: {
          'Accept-Language': 'en-US,en',
          'User-Agent': 'SafeHER-App'
        }
      });
      
      // Convert results to our expected format and calculate distances
      let processedResults = response.data.map(result => {
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);
        let distance = null;
        
        // Calculate distance from user location if available
        if (userLocation) {
          distance = calculateDistance(userLocation.lat, userLocation.lng, lat, lng);
        }
        
        return {
          display_name: result.display_name,
          lat: lat,
          lng: lng,
          type: result.type || 'location',
          distance: distance
        };
      });
      
      // Sort by distance if user location is available
      if (userLocation) {
        processedResults.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
      }
      
      console.log('Processed search results:', processedResults);
      setSearchResults(processedResults);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error searching for location:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Function to use current location from context
  const handleUseCurrentLocation = () => {
    if (userLocation) {
      // Set the map center to current location
      setCenter(userLocation);
      userInteractedWithMapRef.current = false;
      lastPanRef.current = Date.now();
      
      // Force the map to update to the user's location
      if (mapRef.current) {
        mapRef.current.setView([userLocation.lat, userLocation.lng], 15);
      }
      
      // If we're in report mode, use this location for the report
      if (isReportMode) {
        setSelectedLocation(userLocation);
        setReportFormVisible(true);
      }
      
      // Show a single message
      setModalMessage('Centered on your location');
      setShowModal(true);
      setTimeout(() => setShowModal(false), 2000);
    } else {
      setModalMessage('Location not available');
      setShowModal(true);
      setTimeout(() => setShowModal(false), 2000);
    }
  };
  
  // Function to reverse geocode coordinates to get address
  const reverseGeocode = async (lat, lng, setToSearch = false) => {
    try {
      const response = await axios.get('https://nominatim.openstreetmap.org/reverse', {
        params: {
          lat,
          lon: lng,
          format: 'json',
          zoom: 18,
          addressdetails: 1
        },
        headers: {
          'Accept-Language': 'en-US,en'
          // Removed 'User-Agent' header as browsers don't allow setting it from JavaScript
        }
      });
      
      if (response.data && response.data.display_name) {
        // Try to get the most relevant address parts
        const address = response.data.address || {};
        let locationName = '';
        
        // Prioritize: establishment/building → road → suburb → city
        if (address.amenity || address.building || address.shop) {
          locationName = address.amenity || address.building || address.shop;
          if (address.road) {
            locationName += `, ${address.road}`;
          }
        } else if (address.road) {
          locationName = address.road;
          if (address.house_number) {
            locationName = `${address.house_number} ${locationName}`;
          }
        } else if (address.suburb || address.neighbourhood) {
          locationName = address.suburb || address.neighbourhood;
        } else if (address.city || address.town || address.village) {
          locationName = address.city || address.town || address.village;
        } else {
          locationName = response.data.display_name;
        }
        
        // If called from search (setToSearch=true), update search query
        if (setToSearch) {
          setSearchQuery(response.data.display_name);
        }
        
        return locationName;
      }
      
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }
  };
  
  // Create custom icons with different shapes for different location types
  const createCustomIcon = (type) => {
    const colors = {
      support: '#4a90e2',   // Blue for support centers
      health: '#28a745',    // Green for health services
      emergency: '#dc3545', // Red for emergency services
      community: '#6f42c1'  // Purple for community spaces
    };
    
    const shapes = {
      support: 'star',      // Star for support centers
      health: 'diamond',    // Diamond for health services
      emergency: 'triangle', // Triangle for emergency services
      community: 'square'    // Square for community spaces
    };
    
    const shape = shapes[type] || 'star';
    const color = colors[type] || '#4a90e2';
    
    let html = '';
    
    // Create shapes for different location types
    switch(shape) {
      case 'star':
        html = `<div class="marker-star" style="background-color: ${color}"></div>`;
        break;
      case 'diamond':
        html = `<div class="marker-diamond" style="background-color: ${color}"></div>`;
        break;
      case 'triangle':
        html = `<div class="marker-triangle" style="border-bottom-color: ${color}"></div>`;
        break;
      case 'square':
        html = `<div class="marker-square" style="background-color: ${color}"></div>`;
        break;
      default:
        html = `<div class="marker-star" style="background-color: ${color}"></div>`;
    }
    
    return L.divIcon({
      className: `custom-icon ${type}`,
      html: html,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });
  };
  
  // Create user location icon with direction indicator (Uber/Waze-like)
  const createUserIcon = (heading, speed) => {
    // If user is moving (speed > 0.5 m/s) and has heading data, show direction arrow
    const isMoving = speed && speed > 0.5;
    const hasHeading = heading !== null && heading !== undefined && !isNaN(heading);
    
    if (isMoving && hasHeading) {
      // Direction arrow icon
      return L.divIcon({
        className: 'user-location-icon moving',
        html: `
          <div style="
            width: 30px; 
            height: 30px; 
            display: flex; 
            align-items: center; 
            justify-content: center;
            transform: rotate(${heading}deg);
            transition: transform 0.3s ease-out;
          ">
            <svg width="30" height="30" viewBox="0 0 30 30">
              <circle cx="15" cy="15" r="12" fill="#4a90e2" stroke="white" stroke-width="2" opacity="0.3"/>
              <path d="M15 3 L21 21 L15 18 L9 21 Z" fill="#4a90e2" stroke="white" stroke-width="2"/>
            </svg>
          </div>
        `,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });
    } else {
      // Stationary dot icon
      return L.divIcon({
        className: 'user-location-icon stationary',
        html: `
          <div style="
            background-color: #4a90e2; 
            width: 18px; 
            height: 18px; 
            border-radius: 50%; 
            border: 3px solid white; 
            box-shadow: 0 0 8px rgba(74, 144, 226, 0.6);
            animation: pulse 2s infinite;
          "></div>
        `,
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      });
    }
  };
  
  // PERFORMANCE: Memoize user icon to prevent recreation on every render
  const userIcon = useMemo(() => 
    createUserIcon(userLocation?.heading, userLocation?.speed),
    [userLocation?.heading, userLocation?.speed]
  );
  
  // Create custom cluster icons based on marker severity
  const createClusterIcon = useCallback((cluster) => {
    const childCount = cluster.getChildCount();
    
    // Count markers by type to determine cluster severity
    let level1Count = 0;
    let level2Count = 0;
    let level3Count = 0;
    let crimeReportCount = 0;
    
    // Check all child markers for their types
    const markers = cluster.getAllChildMarkers();
    markers.forEach(marker => {
      const report = marker.options.report;
      if (report) {
        if (report.type === 'level_1') level1Count++;
        else if (report.type === 'level_2') level2Count++;
        else if (report.type === 'level_3') level3Count++;
        else if (report.type === 'crime_report') crimeReportCount++;
      }
    });
    
    // Determine cluster class based on highest severity present
    let clusterClass = 'marker-cluster-low';

    // Crime report clusters should be visually distinct from user incident clusters
    const severityCount = level1Count + level2Count + level3Count;
    if (crimeReportCount > 0 && severityCount === 0) {
      clusterClass = 'marker-cluster-crime-report';
    }
    
    // If any high severity reports, use high severity cluster
    if (level3Count > 0) {
      clusterClass = 'marker-cluster-high';
    }
    // If any medium severity reports and no high severity, use medium
    else if (level2Count > 0) {
      clusterClass = 'marker-cluster-medium';
    }
    
    return L.divIcon({
      html: `<div><span>${childCount}</span></div>`,
      className: `marker-cluster ${clusterClass}`,
      iconSize: L.point(40, 40)
    });
  }, []);
  
  // Get marker icon for severity level
  const createSeverityIcon = (type, timestamp = null) => {
    // Color mapping based on severity level
    const severityColors = {
      'level_1': { main: '#28a745', light: '#48d165', dark: '#1d7a33' }, // Green - Low Severity
      'level_2': { main: '#ffc107', light: '#ffd54f', dark: '#c79100' }, // Yellow - Medium Severity
      'level_3': { main: '#dc3545', light: '#ff5565', dark: '#a82835' }, // Red - High Severity
      'default': { main: '#6c757d', light: '#8c959d', dark: '#4c555d' }  // Grey - Default/Unknown
    };
    
    const colors = severityColors[type] || severityColors.default;
    
    // Check if report is recent (within last 24 hours)
    const isRecent = timestamp && (Date.now() - new Date(timestamp).getTime()) < 24 * 60 * 60 * 1000;
    
    // Build className with severity level and recent marker flag
    let className = `custom-icon severity-icon-3d ${type || 'default'}`;
    if (isRecent) {
      className += ' recent-marker';
    }
    
    // Simplified test icon to verify rendering
    const html = `
      <div style="
        width: 30px;
        height: 30px;
        background: ${colors.main};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 5px rgba(0,0,0,0.5);
      "></div>
    `;
    
    return L.divIcon({
      className: className,
      html: html,
      iconSize: [30, 30],
      iconAnchor: [15, 15],
      popupAnchor: [0, -15]
    });
  };
  
  // PERFORMANCE: Memoize default icon to prevent recreation on every render
  const defaultIcon = useMemo(() => createSeverityIcon('default', null), []);

  // Special pulsing icon for the tour spotlight report
  const spotlightIcon = useMemo(() => L.divIcon({
    className: 'custom-icon severity-icon-3d level_3 tour-spotlight-marker',
    html: `
      <div style="position:relative;width:30px;height:30px;">
        <div class="tour-pulse-ring"></div>
        <div style="
          width:30px;height:30px;
          background:#dc3545;
          border:3px solid white;
          border-radius:50%;
          box-shadow:0 2px 8px rgba(220,53,69,0.7);
          position:relative;z-index:1;
        "></div>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15],
  }), []);
  
  // PERFORMANCE: Memoize toggle function
  const toggleReportMode = useCallback(() => {
    setIsReportMode(prev => !prev);
  }, []);
  
  // Handle map click when in report mode
  const handleMapClick = (latlng) => {
    if (!isReportMode) return;
    if (reportClickLockRef.current || reportFormVisible) return;

    reportClickLockRef.current = true;
    setIsReportMode(false);
    setShowReportModeGuide(false);

    const reportId = Date.now().toString();
    const coordsText = `${latlng.lat.toFixed(6)}, ${latlng.lng.toFixed(6)}`;

    setCurrentReport({
      id: reportId,
      position: { lat: latlng.lat, lng: latlng.lng },
      address: coordsText,
      user: user?.name || 'Anonymous',
      timestamp: new Date().toISOString()
    });
    setReportFormVisible(true);
    setIsResolvingReportAddress(true);

    const requestId = ++reverseGeocodeRequestIdRef.current;
    (async () => {
      const address = await reverseGeocode(latlng.lat, latlng.lng);
      if (reverseGeocodeRequestIdRef.current !== requestId) return;
      setCurrentReport(prev => {
        if (!prev || prev.id !== reportId) return prev;
        return { ...prev, address };
      });
      setIsResolvingReportAddress(false);
    })().finally(() => {
      if (reverseGeocodeRequestIdRef.current === requestId) {
        reportClickLockRef.current = false;
      }
    });
  };

  // Show comment form for upvoting
  const showUpvoteCommentForm = (reportId) => {
    if (!user || !user.id) {
      setModalTitle('Authentication Required');
      setModalMessage('You must be logged in to upvote reports.');
      setShowModal(true);
      return;
    }
    
    // Check if user already upvoted this report
    const report = reportMarkers.find(r => r.id === reportId);
    if (report && report.upvotes && report.upvotes.includes(user.id)) {
      setConfirmUnupvoteReportId(reportId);
      return;
    }
    
    // Otherwise show the comment form
    setCommentingOnReport(reportId);
    setCommentText('');
    setShowCommentForm(true);
  };
  
  // Close comment form
  const closeCommentForm = () => {
    setShowCommentForm(false);
    setCommentingOnReport(null);
    setCommentText('');
    commentTextRef.current = ''; // Reset ref too
  };
  
  // Handle upvoting a report
  const handleUpvote = async (reportId, comment = null, isAnonymous = false) => {
    if (!user || !user.id) {
      setModalTitle('Authentication Required');
      setModalMessage('You must be logged in to upvote reports.');
      setShowModal(true);
      return;
    }

    // Validate comment for profanity if provided
    if (comment && comment.trim() !== '') {
      const profanityError = validateNoProfanity(comment, 'Comment');
      if (profanityError) {
        // Increment comment profanity attempt counter
        const newAttempts = commentProfanityAttempts + 1;
        setCommentProfanityAttempts(newAttempts);
        
        // Log violation attempt to backend (this will flag the user)
        // Use setTimeout to make this completely async and not block the warning
        setTimeout(async () => {
          try {
            await api.post('/api/reports/log-profanity-attempt', {
              content: comment,
              type: 'comment'
            });
            console.log('Comment profanity attempt logged to backend');
          } catch (err) {
            console.error('Failed to log profanity attempt:', err);
            // Silently fail - user still sees warning
          }
        }, 0);
        
        // Escalating warning messages for comments
        if (newAttempts >= 3) {
          setModalTitle('FINAL WARNING');
          setModalMessage(
            'This is your final warning. You have attempted to post inappropriate comments ' + newAttempts + ' times.\n\n' +
            'Your account has been flagged for review.\n\n' +
            'Continued attempts to post profanity or offensive language will result in:\n' +
            '• Temporary suspension of commenting privileges\n' +
            '• Permanent ban from SafeHer if violations continue\n\n' +
            'Please remove all inappropriate language and respect our community guidelines.'
          );
        } else {
          setModalTitle('Inappropriate Content Detected');
          setModalMessage(
            profanityError + '\n\n' +
            `Attempt ${newAttempts} of 3. After 3 attempts, your account will be flagged and may face restrictions.\n\n` +
            'SafeHer is a safe space for everyone. Please keep your language respectful.'
          );
        }
        setShowModal(true);
        reportClickLockRef.current = false;
        return; // Stop submission - do NOT allow through
      }
      
      // Reset comment profanity counter on successful validation
      setCommentProfanityAttempts(0);
    }

    try {
      const response = await api.post(`/api/reports/${reportId}/upvote`, { 
        userId: user.id,
        userName: isAnonymous ? 'Anonymous' : user.name,
        comment: comment,
        isAnonymous: isAnonymous
      });
      
      if (response.status === 200) {
        // Update the report in the local state
        const updatedReport = response.data.report;
        
        // Make sure the updated report has the correct comment structure
        if (updatedReport && comment && comment.trim() !== '') {
          // Ensure comments array exists
          if (!updatedReport.comments) {
            updatedReport.comments = [];
          }
          
          // Comments should already be in the API response, but just in case
          // check if the latest comment is there, and if not, add it locally
          const hasNewComment = updatedReport.comments?.some(c => {
            if (!c || typeof c.text !== 'string') return false;
            if (c.text.trim() !== comment.trim()) return false;
            const ts = typeof c.timestamp === 'string' ? Date.parse(c.timestamp) : Number(c.timestamp);
            if (!Number.isFinite(ts)) return true;
            return ts > Date.now() - 15000;
          });
          
          if (!hasNewComment && Array.isArray(updatedReport.comments)) {
            const newComment = {
              id: `temp_${Date.now()}`,
              text: comment,
              userName: isAnonymous ? 'Anonymous' : user.name,
              userId: user.id,
              timestamp: new Date().toISOString(),
              isAnonymous: isAnonymous
            };
            updatedReport.comments.push(newComment);
          }
        }
        
        // Update state with updated report — guard against null/undefined from server
        if (updatedReport) {
          setReportMarkers(reportMarkers.map(report => {
            if (report.id === reportId) {
              return updatedReport;
            }
            return report;
          }));
        } else {
          // Server didn't return updated report (edge case) — just refresh upvote count optimistically
          setReportMarkers(reportMarkers.map(report => {
            if (report.id === reportId) {
              const wasUpvoted = report.upvotes && report.upvotes.includes(user.id);
              return {
                ...report,
                upvoteCount: wasUpvoted ? (report.upvoteCount || 1) - 1 : (report.upvoteCount || 0) + 1,
                upvotes: wasUpvoted
                  ? (report.upvotes || []).filter(uid => uid !== user.id)
                  : [...(report.upvotes || []), user.id],
              };
            }
            return report;
          }));
        }
        
        // Popup content will refresh on next open (avoids duplicate renders / race conditions)
      }
    } catch (error) {
      console.error('Error upvoting report:', error);
      
      // Check if user is banned (403 status)
      if (error.response && error.response.status === 403) {
        setModalTitle('Account Suspended');
        setModalMessage(
          'Your account has been suspended from posting comments due to violations of community guidelines.\n\n' +
          'Reason: Multiple attempts to post inappropriate content.\n\n' +
          'If you believe this is an error, please contact support at support@safeher.com'
        );
      } else if (error.response && error.response.status === 400) {
        const msg = error.response.data?.message || '';
        if (msg.toLowerCase().includes('not found') || msg.toLowerCase().includes('no longer')) {
          setModalTitle('Report Unavailable');
          setModalMessage('This report no longer exists. Please refresh the map to see the latest reports.');
        } else {
          setModalTitle('Error');
          setModalMessage(msg || 'Failed to upvote report. Please try again.');
        }
      } else {
        setModalTitle('Error');
        setModalMessage('Failed to upvote report. Please try again.');
      }
      setShowModal(true);
    }
  };

  // Delete a report
  const deleteReport = async (reportId) => {
    try {
      console.log('Attempting to delete report with ID:', reportId);
      const response = await api.delete(`/api/reports/${reportId}`);
      
      console.log('Delete response:', response.status, response.data);
      
      if (response.status === 200) {
        // Refresh reports from server to ensure deleted report is removed
        console.log('Refreshing reports after deletion...');
        await fetchReports();
        
        setModalTitle('Report Deleted');
        setModalMessage('Your report has been deleted successfully.');
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      console.error('Error details:', error.response?.data || error.message);
      setModalTitle('Error');
      setModalMessage(error.response?.data?.message || 'Failed to delete report. Please try again.');
      setShowModal(true);
    }
  };
  
  // Submit report to the server
  const submitReport = async () => {
    // ── Tour mode: skip API entirely, show fake success ───────────────────
    if (isTourMode) {
      const finalDescription = reportDescriptionRef.current || reportDescription;
      if (!finalDescription) {
        setModalTitle('Missing Information');
        setModalMessage('Please provide a description for your report.');
        setShowModal(true);
        return;
      }
      // Add a fake report pin to the map so it feels real
      const fakeReport = {
        id: `tour-demo-${Date.now()}`,
        position: currentReport?.position || { lat: 37.7749, lng: -122.4194 },
        lat: currentReport?.position?.lat || 37.7749,
        lng: currentReport?.position?.lng || -122.4194,
        latitude: currentReport?.position?.lat || 37.7749,
        longitude: currentReport?.position?.lng || -122.4194,
        type: reportType,
        description: finalDescription,
        timestamp: new Date().toISOString(),
        upvoteCount: 0,
        verifiedByModerator: false,
        createdBy: 'tour-guest',
        creatorName: 'Anonymous',
        source: 'user',
        address: currentReport?.address || 'San Francisco, CA',
      };
      setReportMarkers(prev => [...prev, fakeReport]);
      setReportFormVisible(false);
      setReportDescription('');
      reportDescriptionRef.current = '';
      setCurrentReport(null);
      setIsReportMode(false);
      reportClickLockRef.current = false;
      setModalTitle('Report Submitted');
      setModalMessage('Your report has been added to the map. In the real app it would be reviewed by a moderator. This is the demo — your identity stays completely anonymous.');
      setShowModal(true);
      return;
    }

    // PERFORMANCE: Use ref value (most up-to-date) or fall back to state
    const finalDescription = reportDescriptionRef.current || reportDescription;

    if (currentReport && finalDescription) {
      // Validate description for profanity before submission
      const profanityError = validateNoProfanity(finalDescription, 'Report description');
      if (profanityError) {
        // Increment profanity attempt counter
        const newAttempts = profanityAttempts + 1;
        setProfanityAttempts(newAttempts);
        
        // Log violation attempt to backend (this will flag the user)
        // Use setTimeout to make this completely async and not block the warning
        setTimeout(async () => {
          try {
            const result = await api.post('/api/reports/log-profanity-attempt', {
              content: finalDescription,
              type: 'report'
            });
            if (result.changes > 0) {
              return;
            }
            // Successfully logged attempt
          } catch (err) {
            console.error('Failed to log profanity attempt:', err);
            // Silently fail - user still sees warning
          }
        }, 0);
        
        // Escalating warning messages
        if (newAttempts >= 3) {
          setModalTitle('FINAL WARNING');
          setModalMessage(
            'This is your final warning. You have attempted to submit inappropriate content ' + newAttempts + ' times.\n\n' +
            'Your account has been flagged for review.\n\n' +
            'Continued attempts to post profanity or offensive language will result in:\n' +
            '• Temporary suspension of posting privileges\n' +
            '• Permanent ban from SafeHer if violations continue\n\n' +
            'Please remove all inappropriate language and respect our community guidelines.'
          );
        } else {
          setModalTitle('Inappropriate Content Detected');
          setModalMessage(
            profanityError + '\n\n' +
            `Attempt ${newAttempts} of 3. After 3 attempts, your account will be flagged and may face restrictions.\n\n` +
            'SafeHer is a safe space for everyone. Please keep your language respectful.'
          );
        }
        setShowModal(true);
        return; // Stop submission - do NOT allow through
      }
      
      // Reset profanity counter on successful validation
      setProfanityAttempts(0);
      
      // Define reportData outside try block so it's accessible in catch block
      let reportData = {};
      try {
        reportData = {
          position: currentReport.position,
          address: currentReport.address, // Include human-readable address
          type: reportType,
          description: finalDescription,
          user: isAnonymous ? 'Anonymous' : (user?.name || 'Anonymous'),
          // Track the real creator's ID and name internally
          createdBy: user?.id || null,
          creatorName: user?.name || null,  // Store the real name for self-identification
          // Include subtypes for Level 1, Level 2, and Level 3 reports
          ...(reportType === 'level_1' && { subtype: level1Subtype }),
          ...(reportType === 'level_2' && { subtype: level2Subtype }),
          ...(reportType === 'level_3' && { subtype: level3Subtype })
        };
        
        const response = await api.post('/api/reports', reportData);
        
        if (response.status === 201) {
          // Add the new report to the local state
          const updatedMarkers = [...reportMarkers, response.data.report];
          setReportMarkers(updatedMarkers);
          setReportFormVisible(false);
          setReportDescription('');
          reportDescriptionRef.current = ''; // Reset ref too
          setCurrentReport(null);
          reportClickLockRef.current = false;
          
          // Show special messages based on report type
          if (reportType === 'level_1') {
            // Supportive message for low-severity reports
            setModalTitle('Thank You for Your Report');
            setModalMessage(
              "Even though this was a low-severity situation, your awareness helps build a safer " +
              "community for women. Remember that your experience is valid, and by sharing it, " +
              "you're helping others stay informed."
            );
            setShowModal(true);
          } else if (reportType === 'level_2' && level2Subtype === 'service_refusal') {
            // Business discrimination message with review encouragement
            setModalTitle('Thank You for Reporting Business Discrimination');
            setModalMessage(
              "We're sorry you experienced gender-based discrimination. Your report helps protect " +
              "other women from similar experiences. Consider also leaving a review for this business " +
              "on Google Maps, Yelp, or other review platforms to create broader awareness about " +
              "this issue. Together we can promote inclusive business practices."
            );
            setShowModal(true);
          } else if (reportType === 'level_3') {
            // For high severity reports, show a brief confirmation first
            setModalTitle('Report Submitted');
            setModalMessage('Your high severity report has been submitted. Resources to help you will appear next.');
            setShowModal(true);
            
            // After the user closes the confirmation, show the resources modal
            // We'll set a flag to show resources after modal is closed
            setTimeout(() => {
              // Only show resources after user acknowledges the first message
              const checkModalClosed = setInterval(() => {
                if (!showModal) {
                  setShowResourcesModal(true);
                  clearInterval(checkModalClosed);
                }
              }, 500);
            }, 500);
          } else {
            setModalTitle('Report Submitted');
            setModalMessage('Your report has been submitted successfully.');
            setShowModal(true);
          }
          
          // Refresh reports from server to ensure we have the latest data
          fetchReports();
        }
      } catch (error) {
        console.error('Error submitting report:', error);
        console.error('Error details:', error.response ? error.response.data : 'No response data');
        console.error('Error status:', error.response ? error.response.status : 'No status');
        console.error('Error headers:', error.response ? error.response.headers : 'No headers');
        console.error('Report data attempted to send:', reportData);
        
        // Check if user is banned (403 status)
        if (error.response && error.response.status === 403) {
          setModalTitle('Account Suspended');
          setModalMessage(
            'Your account has been suspended from creating reports due to violations of community guidelines.\n\n' +
            'Reason: Multiple attempts to post inappropriate content.\n\n' +
            'If you believe this is an error, please contact support at support@safeher.com'
          );
        } else {
          setModalTitle('Error');
          setModalMessage(`Failed to submit report: ${error.message}. Please try again.`);
        }
        setShowModal(true);
        reportClickLockRef.current = false;
      }
    } else {
      setModalTitle('Missing Information');
      setModalMessage('Please provide a description for your report.');
      setShowModal(true);
      reportClickLockRef.current = false;
    }
  };
  
  // Cancel report
  const cancelReport = () => {
    reverseGeocodeRequestIdRef.current += 1;
    reportClickLockRef.current = false;
    setIsResolvingReportAddress(false);
    setReportFormVisible(false);
    setCurrentReport(null);
    setReportDescription('');
  };
  
  // Acknowledge a specific report (dismiss)
  const acknowledgeReport = (reportId) => {
    const newAcknowledged = new Set([...acknowledgedReports, reportId]);
    setAcknowledgedReports(newAcknowledged);
    // Save to localStorage
    localStorage.setItem('acknowledgedReports', JSON.stringify([...newAcknowledged]));

    // Update UI immediately (otherwise it waits for the next nearby-reports recalculation)
    setNearbyReports(prev => (Array.isArray(prev) ? prev.filter(r => r?.id !== reportId) : prev));
    if (currentAlertReportId === reportId) {
      setCurrentAlertReportId(null);
    }
  };
  
  // Dismiss all nearby reports
  const dismissAllNearby = () => {
    const nearbyIds = nearbyReports.map(r => r.id);
    const newAcknowledged = new Set([...acknowledgedReports, ...nearbyIds]);
    setAcknowledgedReports(newAcknowledged);
    // Save to localStorage
    localStorage.setItem('acknowledgedReports', JSON.stringify([...newAcknowledged]));
  };
  
  // Clear all acknowledged reports
  const clearAcknowledged = () => {
    setAcknowledgedReports(new Set());
    localStorage.removeItem('acknowledgedReports');
  };
  

  return (
    <div className="map-container fullscreen-map" style={{ 
      opacity: isInitialLoad ? 0.3 : 1, 
      transition: 'opacity 0.3s ease-in' 
    }}>

      {/* Dropdown Menu */}
      {showMenuDropdown && (
        <>
          {/* Backdrop overlay to close menu when clicking outside */}
          <div 
            className="menu-backdrop"
            onClick={() => setShowMenuDropdown(false)}
          ></div>
          
          <div className="menu-dropdown">
            <button 
              className="menu-close-btn"
              onClick={() => setShowMenuDropdown(false)}
            >
              <X size={18} strokeWidth={2.5} />
            </button>

            {/* GPS Status Indicator */}
            {userLocation?.isDefault && (
              <div style={{
                padding: '12px 15px',
                backgroundColor: 'rgba(255, 193, 7, 0.15)',
                borderLeft: '3px solid #ffc107',
                marginBottom: '8px',
                borderRadius: '4px'
              }}>
                <strong style={{ color: '#ffc107', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <MapPin size={16} strokeWidth={2} />
                  Using Default Location
                </strong>
                <p style={{ fontSize: '11px', color: '#DDDFC2', margin: '4px 0 0 0' }}>
                  GPS unavailable. Map centered on San Jose, CA
                </p>
              </div>
            )}

            <div className="menu-items">
              <button
                className="menu-item"
                onClick={() => {
                  setShowMenuDropdown(false);
                  handleUseCurrentLocation();
                }}
              >
                <Navigation size={16} strokeWidth={2} className="menu-icon" />
                <span>Use Current Location</span>
              </button>

              <button
                className="menu-item"
                onClick={() => {
                  setShowMenuDropdown(false);
                  refreshLocation();
                }}
              >
                <MapPin size={16} strokeWidth={2} className="menu-icon" />
                <span>Refresh Location</span>
              </button>

              <div className="menu-divider"></div>

              <button
                className="menu-item"
                onClick={() => {
                  setShowMenuDropdown(false);
                  onNavigateToProfile?.();
                }}
              >
                <Users size={16} strokeWidth={2} className="menu-icon" />
                <span>My Profile</span>
              </button>

              <button
                className="menu-item"
                onClick={() => {
                  setShowMenuDropdown(false);
                  onNavigateToReports?.();
                }}
              >
                <Flag size={16} strokeWidth={2} className="menu-icon" />
                <span>My Reports</span>
              </button>

              <button
                className="menu-item"
                onClick={() => {
                  setShowMenuDropdown(false);
                  setShowResourcesModal(true);
                }}
              >
                <Heart size={16} strokeWidth={2} className="menu-icon" />
                <span>Support Resources</span>
              </button>

              <InstallAppCTA
                className="menu-item"
                label="Install App"
                onBeforeCloseMenu={() => setShowMenuDropdown(false)}
              />

              <div className="menu-divider"></div>

              {(user && (user.role === 'admin' || user.role === 'moderator' || user.moderatorActive || user.moderator)) ? (
                <button
                  className="menu-item"
                  onClick={() => {
                    setShowMenuDropdown(false);
                    onNavigateToModeratorDashboard?.();
                  }}
                >
                  <Shield size={16} strokeWidth={2} className="menu-icon" />
                  <span>Moderator Dashboard</span>
                </button>
              ) : (
                <button
                  className="menu-item"
                  onClick={() => {
                    setShowMenuDropdown(false);
                    setShowModeratorRequest(true);
                  }}
                >
                  <Shield size={16} strokeWidth={2} className="menu-icon" />
                  <span>Request Moderator Access</span>
                </button>
              )}

              <button
                className="menu-item"
                onClick={() => {
                  setShowMenuDropdown(false);
                  onLogout?.();
                }}
              >
                <LogOut size={16} strokeWidth={2} className="menu-icon" />
                <span>Logout</span>
              </button>
            </div>
        </div>
      </>
    )}

    <MapContainer
      center={isTourMode ? [37.7749, -122.4194] : [center.lat, center.lng]}
      zoom={isTourMode ? 13 : 15}
      className="map"
      ref={mapRef}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {/* Map click handler for report mode */}
      <MapClickHandler isReportMode={isReportMode} onMapClick={handleMapClick} />

      <MapInteractionHandler onUserInteract={handleUserMapInteraction} />

      {showModeratorCoverageCircle && moderatorCoverage && Number.isFinite(Number(moderatorCoverage.centerLat)) && Number.isFinite(Number(moderatorCoverage.centerLng)) && Number.isFinite(Number(moderatorCoverage.radiusMeters)) && Number(moderatorCoverage.radiusMeters) > 0 && (
        <Circle
          center={[Number(moderatorCoverage.centerLat), Number(moderatorCoverage.centerLng)]}
          radius={Number(moderatorCoverage.radiusMeters)}
          pathOptions={{ color: '#2C341B', weight: 2, fillColor: '#97AC82', fillOpacity: 0.12 }}
        />
      )}

      {userLocation && (
        <>
          {/* Location trail path (Uber/Waze-like) */}
          {showLocationTrail && locationHistory.length > 1 && (
            <Polyline 
              positions={locationHistory.map(loc => [loc.lat, loc.lng])}
              pathOptions={{
                color: '#4a90e2',
                weight: 3,
                opacity: 0.6,
                lineCap: 'round',
                lineJoin: 'round'
              }}
            />
          )}
          
          {/* Alert radius circle (shows proximity alert radius) */}
          {showAccuracyCircle && (
            <Circle
              center={[userLocation.lat, userLocation.lng]}
              radius={alertRadius}
              pathOptions={{
                fillColor: '#dc3545',
                fillOpacity: 0.1,
                stroke: true,
                color: '#dc3545',
                weight: 1,
                opacity: 0.3
              }}
            />
          )}
            
            {/* Pulsing outer circle for better visibility */}
            <CircleMarker
              center={[userLocation.lat, userLocation.lng]}
              radius={15}
              pathOptions={{
                fillColor: '#4a90e2',
                fillOpacity: 0.15,
                stroke: true,
                color: '#4a90e2',
                weight: 2,
                opacity: 0.4
              }}
            />
            
            {/* User location marker with direction */}
            <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
              <Popup>
                <div className="info-window">
                  <h3>Your Location</h3>
                  {userLocation.accuracy && (
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                      Accuracy: ±{Math.round(userLocation.accuracy)}m
                    </p>
                  )}
                  {userLocation.speed && userLocation.speed > 0.5 && (
                    <p style={{ fontSize: '12px', color: '#666' }}>
                      Speed: {(userLocation.speed * 3.6).toFixed(1)} km/h
                    </p>
                  )}
                </div>
              </Popup>
            </Marker>
            
            {/* Map update on center changes (search only) */}
            <SetViewOnCenter center={center} isSearchTriggered={isSearchTriggered} />
          </>
        )}
        
        {/* Current temporary report marker */}
        {currentReport && (
          <Marker position={[currentReport.position.lat, currentReport.position.lng]} icon={reportType ? createSeverityIcon(reportType, currentReport.timestamp) : defaultIcon}>
            <Popup>
              <div className="info-window">
                <h3>New Report</h3>
                <p>You are reporting an incident at this location.</p>
              </div>
            </Popup>
          </Marker>
        )}
        
        {/* Clustered report markers - optimized for performance */}
        {Array.isArray(reportMarkers) && reportMarkers.length > 0 ? (
          <MarkerClusterGroup
            chunkedLoading={true}
            maxClusterRadius={80} /* Increased for better clustering */
            spiderfyOnMaxZoom={true}
            disableClusteringAtZoom={19} /* Only uncluster at very high zoom */
            showCoverageOnHover={false}
            zoomToBoundsOnClick={true}
            iconCreateFunction={createClusterIcon}
          >
            {reportMarkers
              .filter(report => report && report.position && typeof report.position.lat === 'number' && typeof report.position.lng === 'number')
              .map((report) => (
              <Marker
                key={report.id}
                position={[report.position.lat, report.position.lng]}
                icon={isTourMode && report.id === 'tour-001' ? spotlightIcon : createSeverityIcon(report.type, report.timestamp)}
                report={report} // Pass report to marker for cluster color calculations
                ref={(m) => {
                  if (m) {
                    reportMarkerRefs.current.set(report.id, m);
                  } else {
                    reportMarkerRefs.current.delete(report.id);
                  }
                }}
                eventHandlers={{
                  click: (e) => {
                    // Prevent the click from bubbling to the map and immediately closing the popup
                    e?.originalEvent?.stopPropagation?.();
                    handleUserMapInteraction();

                    // Prevent the viewport-fetch refresh from closing the popup after Leaflet auto-pans
                    suppressReportFetchUntilRef.current = Date.now() + 1200;
                  }
                }}
              >
            <Popup autoPan={true} autoPanPadding={[50, 50]} closeOnClick={false} autoClose={false} keepInView={true}>
              <div className="info-window" style={{ maxHeight: '360px', overflowY: 'auto', paddingRight: '4px' }}>
                <h3 className={`severity-${report.type}`}>
                  {report.type === 'level_1' && 'Level 1: Low Severity'}
                  {report.type === 'level_2' && 'Level 2: Medium Severity'}
                  {report.type === 'level_3' && 'Level 3: High Severity'}
                  {!['level_1', 'level_2', 'level_3'].includes(report.type) && report.type}
                </h3>
                
                {/* Display Level 1 subtype if available */}
                {report.type === 'level_1' && report.subtype && (
                  <p className="incident-subtype level1-subtype-tag">
                    {report.subtype === 'cat_calling' && 'Cat calling'}
                    {report.subtype === 'infrastructure_hazards' && 'Infrastructure & Safety Hazards'}
                    {report.subtype === 'no_security_cameras' && 'No working security cameras'}
                    {report.subtype === 'other_level1' && 'Other'}
                  </p>
                )}
                
                {/* Display Level 3 subtype if available */}
                {report.type === 'level_3' && report.subtype && (
                  <p className="incident-subtype level3-subtype-tag">
                    {report.subtype === 'drink_tampering' && 'Drink tampering/drugging'}
                    {report.subtype === 'coercion_to_leave' && 'Coercion to leave venue'}
                    {report.subtype === 'verbal_threats' && 'Threats/hate speech'}
                    {report.subtype === 'kidnapping_attempt' && 'Kidnapping attempt'}
                    {report.subtype === 'other_level3' && 'Other'}
                  </p>
                )}
                
                {/* Display Level 2 subtype if available */}
                {report.type === 'level_2' && report.subtype && (
                  <p className="incident-subtype level2-subtype-tag">
                    {report.subtype === 'invasive_questions' && 'Invasive questions'}
                    {report.subtype === 'cornered_no_contact' && 'Being cornered without physical contact'}
                    {report.subtype === 'persistent_following' && 'Persistent following after refusal'}
                    {report.subtype === 'service_refusal' && 'Service/entry refused based on gender'}
                    {report.subtype === 'other_level2' && 'Other'}
                  </p>
                )}
                {/* Show the user's real name for their own anonymous reports */}
                {report.user !== 'Anonymous' && <p>Reported by: {report.user}</p>}
                {report.user === 'Anonymous' && report.createdBy === user?.id && (
                  <p>Reported by you <span className="anonymous-tag">(anonymous to others)</span></p>
                )}
                {report.user === 'Anonymous' && report.createdBy !== user?.id && (
                  <p>Reported anonymously</p>
                )}

                {report.address && (
                  <p style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#555' }}>
                    <MapPin size={14} strokeWidth={2} />
                    {report.address}
                  </p>
                )}
                <p>{report.description}</p>
                <p className="report-timestamp">{new Date(report.timestamp).toLocaleString()}</p>
                
                {/* Show upvote count for all reports */}
                <div className="report-upvotes">
                  <Heart size={14} fill="#dc3545" strokeWidth={0} style={{ marginRight: '4px' }} />
                  <span>
                    {report.upvoteCount || 0} {report.upvoteCount === 1 ? 'upvote' : 'upvotes'}
                  </span>
                </div>
                
                {/* Comments section with scrollable container */}
                {report.comments && report.comments.length > 0 && (
                  <div className="report-comments">
                    <h4>
                      <MessageCircle size={16} strokeWidth={2} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      Comments ({report.comments.length})
                    </h4>
                    <div 
                      className="comments-list"
                      style={{
                        maxHeight: '150px',
                        overflowY: 'auto',
                        padding: '5px',
                        border: '1px solid #eee',
                        borderRadius: '4px',
                        marginBottom: '10px'
                      }}
                    >
                      {Array.isArray(report.comments) && report.comments.map(comment => (
                        <div key={comment.id} className="comment-item" style={{
                          marginBottom: '10px',
                          padding: '8px',
                          backgroundColor: '#f9f9f9',
                          borderRadius: '4px',
                          borderLeft: '3px solid #688A65'
                        }}>
                          <div className="comment-header" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '4px'
                          }}>
                            <span className="comment-author" style={{ fontWeight: 'bold', fontSize: '12px' }}>
                              {comment.userName}
                            </span>
                            <span className="comment-date" style={{ fontSize: '11px', color: '#777' }}>
                              {new Date(comment.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <p className="comment-text" style={{ margin: '0', fontSize: '13px' }}>{comment.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Show messages notification for report owner only when this report has messages */}
                {report.createdBy === user?.id && report.hasMessages && (
                  <div style={{
                    backgroundColor: unreadMessagesCount > 0 ? '#fff3cd' : '#d1ecf1',
                    border: `1px solid ${unreadMessagesCount > 0 ? '#ffc107' : '#bee5eb'}`,
                    borderRadius: '6px',
                    padding: '8px 10px',
                    marginBottom: '10px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setUserInboxReportId(report.id);
                    setShowUserInbox(true);
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold', marginBottom: '4px' }}>
                      <MessageCircle size={14} />
                      Message from Safety Team
                    </div>
                    <p style={{ margin: 0, color: unreadMessagesCount > 0 ? '#856404' : '#0c5460' }}>
                      Click here to view your messages.
                    </p>
                  </div>
                )}
                
                <div className="report-actions">
                  {/* Show delete button for user's own reports, including anonymous ones */}
                  {(report.user === user?.name || report.createdBy === user?.id) && (
                    <button 
                      className="delete-report-btn" 
                      onClick={(e) => {
                        e.preventDefault(); // Prevent the popup from closing
                        if (window.confirm('Are you sure you want to delete this report?')) {
                          deleteReport(report.id);
                        }
                      }}
                    >
                      <Trash2 size={16} strokeWidth={2} />
                      Delete Report
                    </button>
                  )}
                  
                  {/* Show upvote button for reports made by other users */}
                  {user && user.id && report.createdBy !== user.id && (
                    <button
                      className={`upvote-btn ${report.upvotes && report.upvotes.includes(user.id) ? 'upvoted' : ''}`}
                      style={report.upvotes && report.upvotes.includes(user.id)
                        ? { backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', color: '#721c24' }
                        : undefined
                      }
                      onClick={(e) => {
                        e.preventDefault(); // Prevent the popup from closing
                        showUpvoteCommentForm(report.id);
                      }}
                    >
                      <Heart
                        size={16}
                        fill={report.upvotes && report.upvotes.includes(user.id) ? 'currentColor' : 'none'}
                        strokeWidth={2}
                      />
                      {report.upvotes && report.upvotes.includes(user.id) ? 'Remove Upvote' : 'Upvote'}
                    </button>
                  )}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
        </MarkerClusterGroup>
      ) : null}
        
        {/* Safety Locations on Map - DISABLED */}
        {/* {Array.isArray(safetyLocations) && safetyLocations.map(location => (
          <Marker 
            key={location.id} 
            position={[location.lat, location.lng]}
            icon={createCustomIcon(location.type)}
            eventHandlers={{
              click: () => setSelectedLocation(location)
            }}
          >
            <Popup className="custom-popup">
              <div className="info-window">
                <h3>{location.name}</h3>
                <p>{location.description}</p>
                <p className="location-type">{serviceLocationTypes.find(t => t.type === location.type)?.label || location.type}</p>
                <button 
                  className="direction-btn"
                  onClick={() => window.open(`https://www.openstreetmap.org/directions?from=${userLocation?.lat || center.lat},${userLocation?.lng || center.lng}&to=${location.lat},${location.lng}`, '_blank')}
                >
                  Get Directions
                </button>
              </div>
            </Popup>
          </Marker>
        ))} */}
      </MapContainer>

      {reportFormVisible && currentReport && (
        <div className="report-form-overlay">
          <div className="report-form">
            <h3>Report an Incident</h3>
            <p style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', color: '#97AC82', fontWeight: '500' }}>
              <MapPin size={16} strokeWidth={2} />
              Location: {currentReport.address || `${currentReport.position.lat.toFixed(6)}, ${currentReport.position.lng.toFixed(6)}`}
            </p>

            {isResolvingReportAddress && (
              <div style={{ marginTop: '-10px', marginBottom: '14px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>Finding address…</div>
                <div style={{ height: '6px', background: '#e9ecef', borderRadius: '999px', overflow: 'hidden' }}>
                  <div style={{ width: '55%', height: '100%', background: '#688A65', borderRadius: '999px' }} />
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Severity Level</label>
              <select 
                value={reportType} 
                onChange={(e) => setReportType(e.target.value)}
                className={`severity-select severity-${reportType}`}
              >
                {severityOptions.map(opt => (
                  <option
                    key={opt.type}
                    value={opt.type}
                    className={`severity-level-${String(opt.type || '').replace('level_', '')}`}
                  >
                    {getSeveritySelectLabel(opt.type)}
                  </option>
                ))}
              </select>
            </div>

            <div className={`form-group ${reportType === 'level_1' ? 'level1-subtype' : reportType === 'level_2' ? 'level2-subtype' : 'level3-subtype'}`}>
              <label>Incident Type</label>
              <select
                value={currentSubtypeValue}
                onChange={(e) => setCurrentSubtypeValue(e.target.value)}
                className={`subtype-select subtype-${reportType === 'level_1' ? 'level1' : reportType === 'level_2' ? 'level2' : 'level3'}`}
              >
                {subtypeOptionsForReportType.map(opt => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea 
                defaultValue={reportDescription}
                onChange={(e) => {
                  reportDescriptionRef.current = e.target.value;
                }}
                onBlur={(e) => setReportDescription(e.target.value)}
                placeholder="Please describe what happened..."
                rows={4}
              ></textarea>
            </div>

            <div className="form-group" style={{ backgroundColor: '#f0f8ff', padding: '12px', borderRadius: '6px', border: '1px solid #688A65' }}>
              <p style={{ margin: 0, color: '#2C341B', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Shield size={18} strokeWidth={2} style={{ color: '#688A65' }} />
                All reports are submitted anonymously to protect your safety and privacy.
              </p>
            </div>

            <div className="form-actions">
              <button className="cancel-btn" onClick={cancelReport}>Cancel</button>
              <button className="submit-btn" onClick={submitReport}>Submit Report</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Report Confirmation Modal */}
      {showModal && modalTitle && (
        <div className="custom-modal-overlay">
          <div className={`custom-modal ${
            modalTitle.includes('WARNING') ? 'warning-modal' : ''
          } ${
            modalTitle.includes('FINAL WARNING') ? 'final-warning-modal' : ''
          } ${
            modalTitle.includes('Account Suspended') ? 'suspension-modal' : ''
          }`}>
            <div className="custom-modal-header">
              <h3>
                {modalTitle.includes('FINAL WARNING') && <AlertTriangle size={20} strokeWidth={2.5} style={{ marginRight: '8px', verticalAlign: 'middle' }} />}
                {modalTitle.includes('Inappropriate Content Detected') && <AlertCircle size={20} strokeWidth={2.5} style={{ marginRight: '8px', verticalAlign: 'middle' }} />}
                {modalTitle.includes('Account Suspended') && <Ban size={20} strokeWidth={2.5} style={{ marginRight: '8px', verticalAlign: 'middle' }} />}
                {modalTitle.includes('Nearby Report Alert') && <Bell size={20} strokeWidth={2.5} style={{ marginRight: '8px', verticalAlign: 'middle' }} />}
                {modalTitle.includes('Report Submitted') && <CheckCircle size={20} strokeWidth={2.5} style={{ marginRight: '8px', verticalAlign: 'middle' }} />}
                {modalTitle.includes('Error') && <XCircle size={20} strokeWidth={2.5} style={{ marginRight: '8px', verticalAlign: 'middle' }} />}
                {modalTitle}
              </h3>
            </div>
            <div className="custom-modal-body">
              <p>{modalMessage}</p>
            </div>
            <div className="custom-modal-footer" style={{ 
              display: 'flex', 
              gap: '10px', 
              flexWrap: 'wrap',
              justifyContent: 'flex-end'
            }}>
              {/* Show Acknowledge button only for proximity alerts */}
              {currentAlertReportId && (
                <button 
                  className="modal-ok-btn" 
                  onClick={() => {
                    acknowledgeReport(currentAlertReportId);
                    setCurrentAlertReportId(null);
                    setShowModal(false);
                  }}
                  style={{
                    backgroundColor: '#688A65',
                    flex: '1',
                    minWidth: '140px'
                  }}
                >
                  <Check size={16} strokeWidth={2} />
                  Acknowledge & Dismiss
                </button>
              )}
              <button 
                className="modal-ok-btn" 
                onClick={() => {
                  setCurrentAlertReportId(null);
                  setShowModal(false);
                }}
                style={{
                  flex: currentAlertReportId ? '0' : '1',
                  minWidth: currentAlertReportId ? '100px' : '140px'
                }}
              >
                {currentAlertReportId ? 'Close' : 'I Understand'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast Notification (for simple messages without title) */}
      {showModal && !modalTitle && (
        <div className="custom-modal-toast">
          <p>{modalMessage}</p>
        </div>
      )}

      {showCommentForm && (
        <div className="custom-modal-overlay">
          <div className="custom-modal">
            <div className="custom-modal-header">
              <h3>Add a comment (optional)</h3>
            </div>
            <div className="custom-modal-body">
              <p style={{ marginTop: 0 }}>
                Your upvote will be anonymous. You can add an optional comment.
              </p>
              <textarea
                value={commentText}
                onChange={(e) => {
                  setCommentText(e.target.value);
                  commentTextRef.current = e.target.value;
                }}
                placeholder="Add a short comment (optional)"
                rows={3}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
            <div className="custom-modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button className="modal-ok-btn" onClick={closeCommentForm} style={{ backgroundColor: '#6c757d' }}>
                Cancel
              </button>
              <button
                className="modal-ok-btn"
                onClick={() => {
                  if (commentingOnReport) {
                    const reportId = commentingOnReport;
                    const finalComment = (commentTextRef.current || commentText || '').trim();
                    closeCommentForm();
                    handleUpvote(reportId, finalComment ? finalComment : null, isCommentAnonymous);
                  }
                }}
                style={{ backgroundColor: '#688A65' }}
              >
                Upvote
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmUnupvoteReportId && (
        <div className="custom-modal-overlay">
          <div className="custom-modal" style={{ border: '1px solid #f5c6cb', backgroundColor: '#f8d7da' }}>
            <div className="custom-modal-header">
              <h3 style={{ color: '#721c24' }}>Remove upvote?</h3>
            </div>
            <div className="custom-modal-body">
              <p style={{ color: '#721c24' }}>Click remove to undo your upvote for this report.</p>
            </div>
            <div className="custom-modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="modal-ok-btn"
                onClick={() => setConfirmUnupvoteReportId(null)}
                style={{ backgroundColor: '#6c757d' }}
              >
                Cancel
              </button>
              <button
                className="modal-ok-btn"
                onClick={() => {
                  const reportId = confirmUnupvoteReportId;
                  setConfirmUnupvoteReportId(null);
                  handleUpvote(reportId, null);
                }}
                style={{ backgroundColor: '#dc3545' }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Resources Modal for High Severity Reports */}
      {showResourcesModal && (
        <div className="custom-modal-overlay resources-overlay">
          <div className="custom-modal resources-modal">
            <div className="custom-modal-header resources-header">
              <h3>Support Resources</h3>
            </div>
            <div className="custom-modal-body resources-body">
              <p className="resources-intro">
                We're sorry you experienced this incident. Your safety matters, and help is available.
                Here are some resources that may assist you:
              </p>
              
              <div className="resources-list">
                <div className="resource-item">
                  <h4>National Sexual Assault Hotline</h4>
                  <p>24/7 confidential support for survivors</p>
                  <a href="tel:1-800-656-4673" className="resource-contact">1-800-656-4673 (HOPE)</a>
                  <a href="https://www.rainn.org/" target="_blank" rel="noopener noreferrer" className="resource-link">Visit Website</a>
                </div>
                
                <div className="resource-item">
                  <h4>National Domestic Violence Hotline</h4>
                  <p>Support for anyone experiencing domestic violence</p>
                  <a href="tel:1-800-799-7233" className="resource-contact">1-800-799-7233</a>
                  <a href="https://www.thehotline.org/" target="_blank" rel="noopener noreferrer" className="resource-link">Visit Website</a>
                </div>
                
                <div className="resource-item">
                  <h4>Crisis Text Line</h4>
                  <p>Text-based mental health support</p>
                  <p className="resource-contact">Text HOME to 741741</p>
                  <a href="https://www.crisistextline.org/" target="_blank" rel="noopener noreferrer" className="resource-link">Visit Website</a>
                </div>
                
                <div className="resource-item">
                  <h4>Local Support Centers</h4>
                  <p>Find nearby resources and support groups</p>
                  <a href="https://centers.rainn.org/" target="_blank" rel="noopener noreferrer" className="resource-link">Find Local Support</a>
                </div>
              </div>
            </div>
            <div className="custom-modal-footer resources-footer">
              <button 
                className="modal-ok-btn resources-close-btn" 
                onClick={() => setShowResourcesModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showAlertsPanel && (
        <div className="alerts-panel" onClick={() => setShowAlertsPanel(false)}>
          <div className="alerts-panel-content" onClick={(e) => e.stopPropagation()}>
            <div className="alerts-panel-header">
              <h3>Proximity Alerts</h3>
              <button
                className="menu-close-btn"
                onClick={() => setShowAlertsPanel(false)}
                aria-label="Close alerts"
                title="Close alerts"
              >
                <X size={18} strokeWidth={2.5} />
              </button>
            </div>
            {Array.isArray(nearbyReports) && nearbyReports.length > 0 ? (
              <>
                <div className="alerts-section">
                  <h4>Active Alerts ({nearbyReports.length})</h4>
                  <div className="alerts-list">
                    {nearbyReports.map((report) => {
                      const distance = userLocation
                        ? calculateDistance(userLocation.lat, userLocation.lng, report.position.lat, report.position.lng)
                        : null;
                      return (
                        <div key={report.id} className={`alert-item severity-${report.type}`}>
                          <div className="alert-header">
                            <span className={`severity-badge ${report.type}`}>{report.type?.replace('_', ' ')}</span>
                            {distance !== null && (
                              <span className="alert-distance">{Math.round(distance)}m</span>
                            )}
                          </div>
                          <div className="alert-description">{report.description}</div>
                          <div className="alert-actions">
                            <button
                              className="alert-action-btn navigate-btn"
                              onClick={() => {
                                if (mapRef.current) {
                                  mapRef.current.setView([report.position.lat, report.position.lng], 16, { animate: true });
                                }
                              }}
                            >
                              Navigate
                            </button>
                            <button
                              className="alert-action-btn ack-btn"
                              onClick={() => acknowledgeReport(report.id)}
                            >
                              Acknowledge
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <button className="dismiss-all-btn" onClick={dismissAllNearby}>
                    Dismiss All Nearby
                  </button>
                </div>
              </>
            ) : (
              <div className="no-alerts">
                <p>No active alerts in your area</p>
                <p className="no-alerts-sub">You're all clear!</p>
              </div>
            )}

            {acknowledgedReports.size > 0 && (
              <div className="alerts-section acknowledged-section">
                <h4>Acknowledged ({acknowledgedReports.size})</h4>
                <button className="clear-ack-btn" onClick={clearAcknowledged}>
                  Clear All
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showReportModeGuide && (
        <div className="report-mode-guide" role="status" aria-live="polite">
          Zoom to the location first, then tap/click the map to place your report.
        </div>
      )}
      
      <div className="right-action-buttons">
        {/* Report Incident Button */}
        <button 
          className={`report-incident-btn ${isReportMode ? 'active' : ''}`}
          onClick={toggleReportMode}
          title={isReportMode ? 'Cancel Report' : 'Report Incident'}
        >
          {isReportMode ? <X size={20} strokeWidth={2.5} /> : <AlertTriangle size={20} strokeWidth={2.5} />}
        </button>

        {/* Hamburger Menu Button */}
        <button 
          className="hamburger-menu-btn"
          onClick={() => setShowMenuDropdown(!showMenuDropdown)}
          aria-label="Menu"
          title="Menu"
        >
          <span className="menu-icon-hamburger">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>

        {/* User Message Notification Button */}
        <button 
          className="notification-btn"
          onClick={() => setShowUserInbox(true)}
          title="Your Messages"
        >
          <MessageCircle size={20} strokeWidth={2.5} />
          {unreadMessagesCount > 0 && <span className="badge">{unreadMessagesCount}</span>}
        </button>

        {/* Alerts Bell Button */}
        <button
          className="alerts-bell-btn"
          onClick={() => setShowAlertsPanel(prev => !prev)}
          aria-label={showAlertsPanel ? 'Close Alerts' : 'View Alerts'}
          title={showAlertsPanel ? 'Close proximity alerts' : 'View proximity alerts'}
        >
          {showAlertsPanel ? <X size={20} strokeWidth={2} /> : <Bell size={20} strokeWidth={2} />}
          {!showAlertsPanel && nearbyReports.length > 0 && (
            <span style={{
              position: 'absolute',
              top: '5px',
              right: '5px',
              backgroundColor: '#dc3545',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 'bold',
              border: '2px solid #2C341B',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              {nearbyReports.length}
            </span>
          )}
        </button>

        {/* Moderator Messages Button - Only shown to approved moderators/admin */}
        {(user?.role === 'admin' || (user?.role === 'moderator' && user?.moderatorActive)) && (
          <button 
            className="mod-notification-btn"
            onClick={openModeratorMessages}
            title="Moderator Messages"
          >
            <Shield size={20} strokeWidth={2.5} />
            {pendingModeratorResponsesCount > 0 && (
              <span className="badge">{pendingModeratorResponsesCount}</span>
            )}
          </button>
        )}
      </div>
      
      {/* Draggable Search Panel */}
      <div 
        ref={panelRef}
        className="search-panel"
        style={{ 
          height: `${panelHeight}px`,
          transition: isDragging ? 'none' : 'height 0.3s ease-out'
        }}
      >
        {/* Drag Handle */}
        <div 
          className="search-panel-handle"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          <div className="handle-bar"></div>
        </div>
        
        {/* Search Input */}
        <div className="search-panel-content">
          <form onSubmit={searchLocation} className="search-panel-form">
            <input
              ref={locationInputRef}
              type="text"
              className="search-panel-input"
              placeholder="Search for a location..."
              defaultValue={searchQuery}
              onChange={(e) => {
                const value = e.target.value;
                
                // Clear previous timeout
                if (searchTimeout.current) {
                  clearTimeout(searchTimeout.current);
                }
                
                // Clear results if input is empty
                if (value === '') {
                  setSearchResults([]);
                  setShowSearchResults(false);
                  setSearchQuery('');
                  return;
                }
                
                // PERFORMANCE: Debounce both search AND state update
                searchTimeout.current = setTimeout(() => {
                  setSearchQuery(value);
                  getLocationPredictions(value);
                }, 300);
              }}
              onFocus={() => {
                // Expand panel when focused
                if (panelHeight < window.innerHeight * 0.5) {
                  setPanelHeight(window.innerHeight * 0.5);
                }
                // Show results if we have them
                if (searchResults.length > 0) {
                  setShowSearchResults(true);
                }
              }}
            />
            <button 
              type="submit" 
              className="search-panel-button"
              disabled={isSearching || !searchQuery}
            >
              {isSearching ? '...' : '→'}
            </button>
          </form>
          
          {/* Search Results */}
          <div className="search-panel-results">
            {showSearchResults && searchResults.length > 0 && (
              <div className="results-list">
                {searchResults.map((result, index) => (
                  <div 
                    key={index}
                    className="result-item"
                    onClick={() => {
                      selectLocation(result);
                      setPanelHeight(80); // Collapse after selection
                    }}
                  >
                    <div className="result-icon">
                      <MapPin size={18} strokeWidth={2} />
                    </div>
                    <div className="result-info">
                      <div className="result-name">{result.display_name}</div>
                      {result.distance && (
                        <div className="result-distance">
                          {result.distance.toFixed(1)} km away
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {showSearchResults && searchResults.length === 0 && searchQuery && !isSearching && (
              <div className="no-results-message">
                No locations found for "{searchQuery}"
              </div>
            )}
            
            {!searchQuery && panelHeight > 150 && (
              <div className="search-panel-placeholder">
                <div className="placeholder-icon">
                  <Search size={32} strokeWidth={1.5} color="#97AC82" />
                </div>
                <div className="placeholder-text">Search for any location</div>
                <div className="placeholder-subtext">Type at least 2 characters to see suggestions</div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Moderator Request Form Modal */}
      {showModeratorRequest && (
        <ModeratorRequestForm 
          onClose={() => setShowModeratorRequest(false)}
          onSuccess={() => {
            setShowModeratorRequest(false);
            setModeratorRequestStatus('pending');
            alert('Moderator request submitted successfully! You will be notified once an admin reviews your application.');
          }}
        />
      )}
      
      {/* User Inbox Modal */}
      {showUserInbox && (
        <UserInbox
          reportId={userInboxReportId}
          onViewReport={openReportPopupFromInbox}
          onClose={() => {
            setShowUserInbox(false);
            setUserInboxReportId(null);
          }}
        />
      )}
    </div>
  );
};

export default MapScreen;
